import { useState, useEffect, useCallback } from 'react';
import { useAuth } from './useAuth';
import { supabase } from '../../backend/services/supabase';

export interface IntegrationStatus {
  id: string;
  user_id: string;
  provider: string;
  status: 'valid' | 'invalid' | 'expired' | 'testing' | 'unknown';
  last_validated_at: string;
  last_success_at: string | null;
  last_error_at: string | null;
  error_message: string | null;
  validation_details: any;
  auto_check_enabled: boolean;
  check_interval_hours: number;
  next_check_at: string;
  has_credentials: boolean;
  credentials_updated_at: string | null;
  needs_validation: boolean;
}

export const useIntegrationStatus = () => {
  const { user } = useAuth();
  const [statuses, setStatuses] = useState<IntegrationStatus[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchStatuses = useCallback(async () => {
    if (!user) {
      setStatuses([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Get the user's session token
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) {
        throw new Error('No session token available');
      }

      const response = await fetch('/api/integration-status', {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch integration status: ${response.status}`);
      }

      const data = await response.json();
      setStatuses(data.statuses || []);
    } catch (err) {
      console.error('Error fetching integration status:', err);
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setLoading(false);
    }
  }, [user]);

  const getStatusForProvider = useCallback((provider: string): IntegrationStatus | null => {
    return statuses.find(status => status.provider === provider) || null;
  }, [statuses]);

  const refreshStatus = useCallback(async (provider?: string) => {
    if (provider) {
      // Refresh specific provider
      const status = getStatusForProvider(provider);
      if (status && status.has_credentials) {
        // Trigger a test for this provider
        try {
          const { data: { session } } = await supabase.auth.getSession();
          if (!session?.access_token) return;

          if (provider === 'meta') {
            await fetch('/api/meta/test-credentials', {
              method: 'POST',
              headers: {
                'Authorization': `Bearer ${session.access_token}`,
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({
                // Use stored credentials from integration_settings
                // The API will fetch them from the database
              })
            });
          }
        } catch (err) {
          console.error(`Error refreshing ${provider} status:`, err);
        }
      }
    }
    
    // Refresh all statuses
    await fetchStatuses();
  }, [getStatusForProvider, fetchStatuses]);

  // Auto-refresh every 5 minutes
  useEffect(() => {
    if (!user) return;

    fetchStatuses();

    const interval = setInterval(() => {
      fetchStatuses();
    }, 5 * 60 * 1000); // 5 minutes

    return () => clearInterval(interval);
  }, [user, fetchStatuses]);

  // Check if credentials have changed (for auto-refresh)
  const checkForCredentialChanges = useCallback(async () => {
    if (!user) return;

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) return;

      // Get current integration settings
      const { data: settings } = await supabase
        .from('integration_settings')
        .select('provider, updated_at')
        .eq('user_id', user.id);

      if (!settings) return;

      // Check if any settings were updated recently (within last minute)
      const recentUpdates = settings.filter(setting => {
        const updatedAt = new Date(setting.updated_at);
        const oneMinuteAgo = new Date(Date.now() - 60 * 1000);
        return updatedAt > oneMinuteAgo;
      });

      if (recentUpdates.length > 0) {
        console.log('🔄 Credentials changed, refreshing status...');
        await fetchStatuses();
      }
    } catch (err) {
      console.error('Error checking for credential changes:', err);
    }
  }, [user, fetchStatuses]);

  // Check for changes every 30 seconds
  useEffect(() => {
    if (!user) return;

    const interval = setInterval(checkForCredentialChanges, 30 * 1000);
    return () => clearInterval(interval);
  }, [user, checkForCredentialChanges]);

  return {
    statuses,
    loading,
    error,
    getStatusForProvider,
    refreshStatus,
    refetch: fetchStatuses
  };
};
